/**
 * Small Intl-based formatters. Native `Intl.*` (no date-fns) per the
 * minimum-deps policy; same fidelity for the scope we need.
 */

const RELATIVE = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

const ABSOLUTE = new Intl.DateTimeFormat('en', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const DATE_ONLY = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

/** "just now", "3 minutes ago", "2 days ago", … */
export function relativeTime(value: string | Date): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  const diffSec = Math.round((Date.now() - date.getTime()) / 1000);

  if (Number.isNaN(diffSec)) return '—';
  if (diffSec < 45) return 'just now';
  if (diffSec < 3600) return RELATIVE.format(-Math.round(diffSec / 60), 'minute');
  if (diffSec < 86_400) return RELATIVE.format(-Math.round(diffSec / 3600), 'hour');
  if (diffSec < 2_592_000) return RELATIVE.format(-Math.round(diffSec / 86_400), 'day');
  if (diffSec < 31_536_000) return RELATIVE.format(-Math.round(diffSec / 2_592_000), 'month');
  return RELATIVE.format(-Math.round(diffSec / 31_536_000), 'year');
}

/** "Apr 19, 2026, 01:58 PM" — useful as a `title=` tooltip next to relativeTime. */
export function absoluteDateTime(value: string | Date): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';
  return ABSOLUTE.format(date);
}

/** "19 Apr 2026" — a date a list column is scanned by, without the clock. */
export function absoluteDate(value: string | Date): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';
  return DATE_ONLY.format(date);
}

const THB = new Intl.NumberFormat('en', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const THB_EXACT = new Intl.NumberFormat('en', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

function baht(formatter: Intl.NumberFormat, value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '—';
  return `฿${formatter.format(n)}`;
}

/**
 * A THB amount from the API's decimal string at exactly two places:
 * "฿0.12", "฿1,234.50". Every total, ledger figure, and summary cell; a
 * costed event is never under ฿0.01, so nothing it shows rounds to zero.
 */
export function thb(value: string | number | null | undefined): string {
  return baht(THB, value);
}

/**
 * A THB amount with up to four places: "฿0.0007". Only a line's amount on
 * the task Cost tab and a unit price, where the exact figure is the point.
 */
export function thbExact(value: string | number | null | undefined): string {
  return baht(THB_EXACT, value);
}

const QUANTITY = new Intl.NumberFormat('en', { maximumFractionDigits: 3 });

/** A metered quantity: integers grouped, fractions to three places. */
export function quantity(value: string | number): string {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? QUANTITY.format(n) : String(value);
}

/** A per-unit price shown per million, which is how vendors quote tokens. */
export function perMillion(pricePerUnit: string | number | null): string {
  if (pricePerUnit === null) return '—';
  const n = typeof pricePerUnit === 'number' ? pricePerUnit : Number(pricePerUnit);
  return Number.isFinite(n) ? `${THB_EXACT.format(n * 1_000_000)}/M` : '—';
}

/** The head and tail of a long id, for a cell that names a record without filling the row. */
export function shortId(id: string): string {
  return id.length > 13 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}
